import React, { useState, useEffect } from 'react';
import { 
  DollarSign, TrendingUp, TrendingDown, RefreshCw, Loader,
  ArrowRightLeft, Globe, Calculator, Clock, ChevronDown, ChevronUp, X
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import PageAwareAI from './PageAwareAI';

const API_URL = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

const ForexDashboard = () => {
  const [forexData, setForexData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState('major');
  const [selectedPair, setSelectedPair] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false); // For mobile modal
  
  // Currency converter state
  const [fromCurrency, setFromCurrency] = useState('USD');
  const [toCurrency, setToCurrency] = useState('EUR');
  const [amount, setAmount] = useState('1000');
  const [convertedAmount, setConvertedAmount] = useState(null);
  const [showConverter, setShowConverter] = useState(false);

  const currencies = ['USD', 'EUR', 'GBP', 'JPY', 'CHF', 'AUD', 'CAD', 'NZD', 'SGD', 'HKD', 'INR', 'MXN'];

  useEffect(() => {
    fetchForexData();
  }, []);

  useEffect(() => {
    if (selectedPair) {
      fetchHistory(selectedPair.fromCurrency, selectedPair.toCurrency);
    }
  }, [selectedPair]);

  const fetchForexData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/forex`);
      if (!response.ok) throw new Error('Failed to fetch forex data');
      const data = await response.json();
      setForexData(data);
      
      // Set default selected pair
      if (data.major && data.major.length > 0) {
        setSelectedPair(data.major[0]);
      }
    } catch (err) {
      console.error('Forex fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async (from, to) => {
    setHistoryLoading(true);
    try {
      const response = await fetch(`${API_URL}/forex/history/${from}/${to}?days=30`);
      if (!response.ok) throw new Error('Failed to fetch history');
      const data = await response.json();
      setHistoryData(data);
    } catch (err) {
      console.error('History fetch error:', err);
      setHistoryData(null);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleConvert = async () => {
    try {
      const response = await fetch(`${API_URL}/forex/convert?from=${fromCurrency}&to=${toCurrency}&amount=${amount}`);
      if (!response.ok) throw new Error('Conversion failed');
      const data = await response.json();
      setConvertedAmount(data);
    } catch (err) {
      console.error('Conversion error:', err);
    }
  };

  const swapCurrencies = () => {
    const temp = fromCurrency;
    setFromCurrency(toCurrency);
    setToCurrency(temp);
    setConvertedAmount(null);
  };

  const getCategoryData = () => {
    if (!forexData) return [];
    return forexData[activeCategory] || [];
  };

  const formatRate = (rate, pair) => {
    if (!rate) return 'N/A';
    if (pair.includes('JPY')) {
      return rate.toFixed(2);
    }
    return rate.toFixed(4);
  };

  const handlePairClick = (pair) => {
    setSelectedPair(pair);
    setShowDetails(true); // Show modal on mobile
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '4rem' }}>
        <Loader size={48} color="#60a5fa" className="spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        background: 'rgba(239, 68, 68, 0.1)', 
        border: '1px solid rgba(239, 68, 68, 0.3)', 
        borderRadius: '0.75rem', 
        padding: '2rem',
        textAlign: 'center'
      }}>
        <Globe size={48} color="#f87171" style={{ margin: '0 auto 1rem' }} />
        <h3 style={{ color: '#f87171', marginBottom: '0.5rem' }}>Error Loading Data</h3>
        <p style={{ color: '#fca5a5' }}>{error}</p>
        <button onClick={fetchForexData} style={{
          marginTop: '1rem',
          padding: '0.5rem 1rem',
          background: '#ef4444',
          border: 'none',
          borderRadius: '0.5rem',
          color: 'white',
          cursor: 'pointer'
        }}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, #0ea5e9 0%, #0369a1 100%)', 
        borderRadius: '0.75rem', 
        padding: '1.25rem', 
        marginBottom: '1rem'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
              <Globe size={24} />
              Forex Dashboard
            </h2>
            <p style={{ color: '#bae6fd', fontSize: '0.75rem', margin: 0 }}>
              Live currency exchange rates
            </p>
          </div>
          <button
            onClick={fetchForexData}
            disabled={loading}
            style={{
              padding: '0.5rem 1rem',
              background: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '0.5rem',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontWeight: '600',
              fontSize: '0.875rem'
            }}
          >
            <RefreshCw size={16} className={loading ? 'spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Currency Converter Toggle */}
      <button
        onClick={() => setShowConverter(!showConverter)}
        style={{
          width: '100%',
          padding: '0.75rem',
          background: 'rgba(30, 41, 59, 0.5)',
          border: '1px solid #334155',
          borderRadius: '0.5rem',
          color: '#60a5fa',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          marginBottom: '1rem',
          fontSize: '0.875rem',
          fontWeight: '600'
        }}
      >
        <Calculator size={18} />
        Currency Converter
        {showConverter ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>

      {/* Currency Converter (Collapsible) */}
      {showConverter && (
        <div style={{
          background: 'rgba(30, 41, 59, 0.5)',
          border: '1px solid #334155',
          borderRadius: '0.75rem',
          padding: '1rem',
          marginBottom: '1rem'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {/* Amount Input */}
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Amount</label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: '#1e293b',
                  border: '1px solid #475569',
                  borderRadius: '0.5rem',
                  color: 'white',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Currency Selectors Row */}
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>From</label>
                <select
                  value={fromCurrency}
                  onChange={(e) => setFromCurrency(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: '#1e293b',
                    border: '1px solid #475569',
                    borderRadius: '0.5rem',
                    color: 'white',
                    fontSize: '0.875rem'
                  }}
                >
                  {currencies.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <button
                onClick={swapCurrencies}
                style={{
                  padding: '0.75rem',
                  background: '#334155',
                  border: 'none',
                  borderRadius: '0.5rem',
                  color: '#60a5fa',
                  cursor: 'pointer',
                  marginTop: '1.25rem'
                }}
              >
                <ArrowRightLeft size={18} />
              </button>

              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>To</label>
                <select
                  value={toCurrency}
                  onChange={(e) => setToCurrency(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: '#1e293b',
                    border: '1px solid #475569',
                    borderRadius: '0.5rem',
                    color: 'white',
                    fontSize: '0.875rem'
                  }}
                >
                  {currencies.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            {/* Convert Button */}
            <button
              onClick={handleConvert}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'linear-gradient(135deg, #0ea5e9 0%, #0369a1 100%)',
                border: 'none',
                borderRadius: '0.5rem',
                color: 'white',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '600'
              }}
            >
              Convert
            </button>
          </div>
          
          {/* Conversion Result */}
          {convertedAmount && (
            <div style={{
              marginTop: '0.75rem',
              padding: '0.75rem',
              background: 'rgba(14, 165, 233, 0.1)',
              border: '1px solid rgba(14, 165, 233, 0.3)',
              borderRadius: '0.5rem'
            }}>
              <div style={{ fontSize: '1.125rem', fontWeight: 'bold', color: '#0ea5e9' }}>
                {parseFloat(amount).toLocaleString()} {fromCurrency} = {convertedAmount.convertedAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })} {toCurrency}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '0.25rem' }}>
                Rate: 1 {fromCurrency} = {convertedAmount.rate.toFixed(4)} {toCurrency}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Category Tabs - Horizontal Scroll */}
      <div style={{ 
        display: 'flex', 
        gap: '0.5rem', 
        marginBottom: '1rem', 
        overflowX: 'auto',
        paddingBottom: '0.5rem',
        WebkitOverflowScrolling: 'touch'
      }}>
        {[
          { key: 'major', label: 'Major Pairs' },
          { key: 'cross', label: 'Cross Pairs' },
          { key: 'exotic', label: 'Exotic Pairs' },
          { key: 'crypto', label: 'Crypto' }
        ].map(cat => (
          <button
            key={cat.key}
            onClick={() => setActiveCategory(cat.key)}
            style={{
              padding: '0.625rem 1rem',
              background: activeCategory === cat.key ? 'rgba(14, 165, 233, 0.2)' : 'rgba(51, 65, 85, 0.5)',
              border: activeCategory === cat.key ? '1px solid rgba(14, 165, 233, 0.5)' : '1px solid #475569',
              borderRadius: '0.5rem',
              color: activeCategory === cat.key ? '#0ea5e9' : '#cbd5e1',
              cursor: 'pointer',
              fontSize: '0.8rem',
              fontWeight: '600',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Currency Pairs List */}
      <div style={{
        background: 'rgba(30, 41, 59, 0.5)',
        border: '1px solid #334155',
        borderRadius: '0.75rem',
        padding: '1rem'
      }}>
        <h3 style={{ fontSize: '1rem', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0 0 0.75rem 0' }}>
          <DollarSign size={18} color="#60a5fa" />
          {activeCategory.charAt(0).toUpperCase() + activeCategory.slice(1)} Currency Pairs
        </h3>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {getCategoryData().map((pair, idx) => (
            <div
              key={idx}
              onClick={() => handlePairClick(pair)}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.875rem',
                background: selectedPair?.pair === pair.pair ? 'rgba(14, 165, 233, 0.1)' : 'rgba(51, 65, 85, 0.3)',
                border: selectedPair?.pair === pair.pair ? '1px solid rgba(14, 165, 233, 0.3)' : '1px solid #475569',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              <div>
                <div style={{ fontWeight: '600', fontSize: '0.95rem', marginBottom: '0.125rem' }}>
                  {pair.pair}
                </div>
                <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                  {pair.name}
                </div>
              </div>
              
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontWeight: '600', fontSize: '1rem' }}>
                  {formatRate(pair.rate, pair.pair)}
                </div>
                <div style={{ 
                  fontSize: '0.75rem', 
                  color: pair.changePercent >= 0 ? '#34d399' : '#f87171',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: '0.25rem'
                }}>
                  {pair.changePercent >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                  {pair.changePercent >= 0 ? '+' : ''}{pair.changePercent?.toFixed(2)}%
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Details Modal (Mobile-friendly bottom sheet) */}
      {showDetails && selectedPair && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'flex-end'
          }}
          onClick={() => setShowDetails(false)}
        >
          <div 
            style={{
              width: '100%',
              maxHeight: '80vh',
              background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
              borderRadius: '1rem 1rem 0 0',
              padding: '1rem',
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Handle bar */}
            <div style={{ 
              width: '40px', 
              height: '4px', 
              background: '#475569', 
              borderRadius: '2px',
              margin: '0 auto 1rem'
            }} />

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
              <div>
                <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>{selectedPair.pair}</h3>
                <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: '0.25rem 0 0 0' }}>{selectedPair.name}</p>
              </div>
              <button
                onClick={() => setShowDetails(false)}
                style={{
                  background: '#334155',
                  border: 'none',
                  borderRadius: '0.5rem',
                  padding: '0.5rem',
                  color: 'white',
                  cursor: 'pointer'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Rate Info Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
              <div style={{ 
                background: 'rgba(34, 197, 94, 0.1)', 
                border: '1px solid rgba(34, 197, 94, 0.3)',
                borderRadius: '0.5rem', 
                padding: '0.75rem',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginBottom: '0.25rem' }}>BID</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#34d399' }}>
                  {formatRate(selectedPair.bidPrice, selectedPair.pair)}
                </div>
              </div>
              <div style={{ 
                background: 'rgba(239, 68, 68, 0.1)', 
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '0.5rem', 
                padding: '0.75rem',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginBottom: '0.25rem' }}>ASK</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#f87171' }}>
                  {formatRate(selectedPair.askPrice, selectedPair.pair)}
                </div>
              </div>
            </div>

            {/* Additional Info */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr 1fr', 
              gap: '0.5rem',
              marginBottom: '1rem'
            }}>
              <div style={{ textAlign: 'center', padding: '0.5rem', background: 'rgba(51, 65, 85, 0.3)', borderRadius: '0.5rem' }}>
                <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>Spread</div>
                <div style={{ fontSize: '0.875rem', fontWeight: '600' }}>{selectedPair.spreadPips} pips</div>
              </div>
              <div style={{ textAlign: 'center', padding: '0.5rem', background: 'rgba(51, 65, 85, 0.3)', borderRadius: '0.5rem' }}>
                <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>24h High</div>
                <div style={{ fontSize: '0.875rem', fontWeight: '600' }}>{formatRate(selectedPair.high24h, selectedPair.pair)}</div>
              </div>
              <div style={{ textAlign: 'center', padding: '0.5rem', background: 'rgba(51, 65, 85, 0.3)', borderRadius: '0.5rem' }}>
                <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>24h Low</div>
                <div style={{ fontSize: '0.875rem', fontWeight: '600' }}>{formatRate(selectedPair.low24h, selectedPair.pair)}</div>
              </div>
            </div>

            {/* Chart */}
            {historyLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                <Loader size={32} color="#60a5fa" className="spin" />
              </div>
            ) : historyData && historyData.history && historyData.history.length > 0 ? (
              <div>
                <h4 style={{ fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem', margin: '0 0 0.5rem 0' }}>
                  30-Day History
                </h4>
                <div style={{ height: '180px', minHeight: '180px', width: '100%' }}>
                  <ResponsiveContainer width="100%" height={180}>
                    <LineChart data={historyData.history}>
                      <XAxis 
                        dataKey="date" 
                        tick={{ fill: '#64748b', fontSize: 9 }}
                        tickFormatter={(val) => val ? val.slice(5) : ''}
                        interval="preserveStartEnd"
                      />
                      <YAxis 
                        domain={['auto', 'auto']}
                        tick={{ fill: '#64748b', fontSize: 9 }}
                        width={50}
                        tickFormatter={(val) => val.toFixed(3)}
                      />
                      <Tooltip
                        contentStyle={{
                          background: '#1e293b',
                          border: '1px solid #475569',
                          borderRadius: '0.5rem',
                          fontSize: '0.75rem'
                        }}
                        labelStyle={{ color: '#94a3b8' }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="rate" 
                        stroke="#0ea5e9" 
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ) : (
              <div style={{ padding: '1rem', textAlign: 'center', color: '#64748b', fontSize: '0.875rem' }}>
                No historical data available
              </div>
            )}

            {/* Live indicator */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              gap: '0.5rem',
              marginTop: '1rem',
              padding: '0.5rem',
              background: selectedPair.isLive ? 'rgba(34, 197, 94, 0.1)' : 'rgba(251, 191, 36, 0.1)',
              borderRadius: '0.5rem',
              fontSize: '0.75rem',
              color: selectedPair.isLive ? '#34d399' : '#fbbf24'
            }}>
              <div style={{ 
                width: '8px', 
                height: '8px', 
                borderRadius: '50%', 
                background: selectedPair.isLive ? '#34d399' : '#fbbf24'
              }} />
              {selectedPair.isLive ? 'Live Data' : 'Cached Data'}
            </div>
          </div>
        </div>
      )}

      {/* Market Hours Info */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.1) 0%, rgba(6, 182, 212, 0.05) 100%)',
        border: '1px solid rgba(14, 165, 233, 0.2)',
        borderRadius: '0.75rem',
        padding: '1rem',
        marginTop: '1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
          <Clock size={16} color="#0ea5e9" />
          <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>Forex Market Hours (EST)</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem', fontSize: '0.75rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem', background: 'rgba(51, 65, 85, 0.3)', borderRadius: '0.25rem' }}>
            <span style={{ color: '#94a3b8' }}>Sydney</span>
            <span>5pm - 2am</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem', background: 'rgba(51, 65, 85, 0.3)', borderRadius: '0.25rem' }}>
            <span style={{ color: '#94a3b8' }}>Tokyo</span>
            <span>7pm - 4am</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem', background: 'rgba(51, 65, 85, 0.3)', borderRadius: '0.25rem' }}>
            <span style={{ color: '#94a3b8' }}>London</span>
            <span>3am - 12pm</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem', background: 'rgba(51, 65, 85, 0.3)', borderRadius: '0.25rem' }}>
            <span style={{ color: '#94a3b8' }}>New York</span>
            <span>8am - 5pm</span>
          </div>
        </div>
      </div>

      {/* AI Assistant */}
    <PageAwareAI 
  pageId="forex"
  pageData={{
    pairs: forexData?.pairs || [],
    selectedPair: selectedPair,
    rates: forexData?.pairs?.reduce((acc, p) => ({ ...acc, [p.pair]: p.rate }), {}),
    activeCategory: activeCategory,
    converter: {
      from: fromCurrency,
      to: toCurrency,
      amount: amount
    }
  }}
/>
    </div>
  );
};

export default ForexDashboard;